// Company Context Provider for React
// Location: admin-ui/src/contexts/CompanyContext.tsx

import React, { createContext, useState, useContext, useEffect } from 'react';
import { api, companyApi } from '../services';

interface Company {
  id: number;
  name: string;
  is_default?: boolean;
  settings?: {
    logo?: string;
    primaryColor?: string;
    companyDisplayName?: string;
  };
}

interface CompanyContextType {
  currentCompany: Company | null;
  companies: Company[];
  switchCompany: (companyId: number) => Promise<void>;
  loading: boolean;
  error: string | null;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const CompanyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const response = await companyApi.getCompanies();
      setCompanies(response);
      
      // Set default company (first one or from localStorage)
      const savedCompanyId = localStorage.getItem('currentCompanyId');
      const defaultCompany = savedCompanyId 
        ? response.find((c: Company) => c.id === parseInt(savedCompanyId))
        : response.find((c: Company) => c.is_default) || response[0];
      
      if (defaultCompany) {
        setCurrentCompany(defaultCompany);
        localStorage.setItem('currentCompanyId', defaultCompany.id.toString());
      }
    } catch (err) {
      console.error('Error loading companies:', err);
      
      // Set a default company for development/testing
      const defaultCompany = {
        id: 1,
        name: 'Default Company',
        is_default: true,
        settings: {
          companyDisplayName: 'Default Company',
          primaryColor: '#1976d2'
        }
      };
      
      setCompanies([defaultCompany]);
      setCurrentCompany(defaultCompany);
      localStorage.setItem('currentCompanyId', defaultCompany.id.toString());
      
      // Clear error since we have a working fallback
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  const switchCompany = async (companyId: number) => {
    try {
      const company = companies.find(c => c.id === companyId);
      if (!company) throw new Error('Company not found');

      // Call API to switch company
      await companyApi.switchCompany(companyId);
      
      // Update local state
      setCurrentCompany(company);
      localStorage.setItem('currentCompanyId', companyId.toString());
      
      // Reload the page to refresh all data
      window.location.reload();
    } catch (err) {
      setError('Failed to switch company');
      console.error('Error switching company:', err);
    }
  };

  return (
    <CompanyContext.Provider value={{ currentCompany, companies, switchCompany, loading, error }}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within CompanyProvider');
  }
  return context;
};
